<template>
  <div
    v-if="modelValue"
    class="native-drawer-backdrop"
    role="presentation"
    @click.self="requestClose"
  >
    <section
      class="native-drawer"
      role="dialog"
      aria-modal="true"
      :aria-label="title || '对话框'"
      :style="{ width: drawerWidth }"
      @keydown.esc.stop.prevent="requestClose"
    >
      <header class="native-drawer-header">
        <slot name="header">
          <h2>{{ title }}</h2>
        </slot>
        <button class="native-icon-button" type="button" aria-label="关闭" @click="requestClose">
          ×
        </button>
      </header>
      <div class="native-drawer-body">
        <slot />
      </div>
      <footer v-if="$slots.footer" class="native-drawer-footer">
        <slot name="footer" />
      </footer>
    </section>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";

const props = withDefaults(
  defineProps<{
    modelValue: boolean;
    title?: string;
    size?: string | number;
    beforeClose?: (done: () => void) => void;
  }>(),
  { title: "", size: "600px" },
);

const emit = defineEmits<{
  (event: "update:modelValue", value: boolean): void;
  (event: "close"): void;
}>();

const drawerWidth = computed(() => {
  if (typeof props.size === "number") return `${props.size}px`;
  return props.size.includes("%") ? `min(${props.size}, 100vw)` : props.size;
});

const requestClose = () => {
  const done = () => {
    emit("update:modelValue", false);
    emit("close");
  };

  if (props.beforeClose) props.beforeClose(done);
  else done();
};
</script>

<style>
.native-drawer-backdrop {
  position: fixed;
  z-index: 1000;
  inset: 0;
  display: flex;
  justify-content: flex-end;
  background: rgb(15 23 42 / 45%);
}

.native-drawer {
  display: flex;
  flex-direction: column;
  width: min(600px, 100vw);
  height: 100%;
  background: #fff;
  box-shadow: -8px 0 32px rgb(15 23 42 / 18%);
  animation: native-drawer-enter 180ms ease-out;
}

.native-drawer-header,
.native-drawer-footer {
  flex: 0 0 auto;
  padding: 16px 20px;
  border-bottom: 1px solid #e5e7eb;
}

.native-drawer-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.native-drawer-header h2,
.native-drawer-header h4 {
  margin: 0;
}

.native-drawer-body {
  flex: 1;
  min-height: 0;
  overflow: auto;
}

.native-drawer-footer {
  border-top: 1px solid #e5e7eb;
  border-bottom: 0;
}

.native-icon-button {
  border: 0;
  background: transparent;
  color: #64748b;
  font-size: 1.5rem;
  cursor: pointer;
}

@keyframes native-drawer-enter {
  from { transform: translateX(24px); opacity: 0.7; }
  to { transform: translateX(0); opacity: 1; }
}

@media (max-width: 640px) {
  .native-drawer {
    width: 100vw !important;
  }
}
</style>
